import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import classname from 'classnames';
import { useClickOutside } from './useClickOutside';
import Fontselect from './Fontselect';
import Fontsizeselect from './Fontsizeselect';
import Variables from './Variables';
import UploadImg from './UploadImg';
import Attachment from './Attachment';
import Color from './Color';
import Bold from './Bold';
import { Italic, Underline, Strikethrough } from './Bold';
import Link from './Link';
import Unsubscribe from './Unsubscription';
import Signature from './Signature';
import Preview from './Preview';
import Split from './Split';
import AlignItems from './AlignItems';
import Lineheight from './Lineheight';
import { ULLists, OLLists } from './Lists';
import { Indent, Outdent } from './Indent';
import { ExtendA, Extend, AutoExtend } from './Extend';
import { Undo, Redo } from './Undo1';
import Emoji from './Emoji';
import Table from './Table1';
import Formatpainter, { Removeformat, Code } from './Formatpainter1';
import AIWriter from './AIWriter';
import * as EditorTypeAll from '../tinymce.d';
import editorToolbarEmitter from '@/events/editor-toolbar';
// import AIDropdown from '@/components/ai-writer/ai-dropdown';
// import { useStore } from '@/store';

const compListsOrg = {
  fontselect: { comp: Fontselect, param: {} },
  fontsizeselect: { comp: Fontsizeselect, param: {} },
  variables: { comp: Variables, param: {} },
  aiWriter: { comp: AIWriter, param: {} },
  uploadImg: { comp: UploadImg, param: {} },
  attachment: {
    comp: Attachment,
    param: {
      allowedFileTypes: [
        'image/*',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ],
    },
  },
  fontcolor: { comp: Color, param: { type: 'fontcolor' } },
  backcolor: { comp: Color, param: { type: 'backcolor' } },
  bold: { comp: Bold, param: {} },
  link: { comp: Link, param: {} },
  preview: { comp: Preview, param: {} },
  split: { comp: Split, param: {} },
  italic: { comp: Italic, param: {} },
  underline: { comp: Underline, param: {} },
  strikethrough: { comp: Strikethrough, param: {} },
  alignItems: { comp: AlignItems, param: {} },
  lineheight: { comp: Lineheight, param: {} },
  ullists: { comp: ULLists, param: {} },
  ollists: { comp: OLLists, param: {} },
  indent: { comp: Indent, param: {} },
  outdent: { comp: Outdent, param: {} },
  emoji: { comp: Emoji, param: {} },
  undo: { comp: Undo, param: {} },
  redo: { comp: Redo, param: {} },
  formatpainter: { comp: Formatpainter, param: {} },
  removeformat: { comp: Removeformat, param: {} },
  table: { comp: Table, param: {} },
  code: { comp: Code, param: {} },
  unsubscribe: { comp: Unsubscribe, param: {} },
  signature: { comp: Signature, param: {} },
};

export type ToolbarAlwaysType = (keyof typeof compListsOrg | 'extend' | 'extendA' | 'autoExtend' | 'attachment')[];

interface ToolbarItemsRendererProps {
  items: ToolbarAlwaysType;
  compLists: Record<string, { comp: React.ComponentType<any>; param: any }>;
  editorInstance: EditorTypeAll.Editor;
  allowedFileTypes: string[];
  keyPrefix: string;
}

// Toolbar Items Renderer Component
const ToolbarItemsRenderer = ({
  items,
  compLists,
  editorInstance,
  allowedFileTypes,
  keyPrefix,
}: ToolbarItemsRendererProps) => {
  return (
    <>
      {items.map((i, index) => {
        const item = compLists[i];
        if (!item) return null;
        const Component = item.comp;
        const param = item.param;
        return (
          <Component
            key={`${keyPrefix}-${i}-${index}`}
            editorInstance={editorInstance}
            {...param}
            allowedFileTypes={allowedFileTypes}
          />
        );
      })}
    </>
  );
};

interface Props {
  allowedFileTypes: string[];
  toolbarAlways: ToolbarAlwaysType;
  toolbarExtendA: ToolbarAlwaysType;
  toolbarExtend: ToolbarAlwaysType;
  toolbarAutoExtend?: ToolbarAlwaysType;
  /** 自动扩展工具栏第二行（当空间不足时，浮层分两排显示） */
  toolbarAutoExtendRow2?: ToolbarAlwaysType;
  editorInstance: EditorTypeAll.Editor;
  attachment_upload_handler?: (files: FileList) => void;
  className?: string;
  height?: number;
  customToolbarPlugin?: React.ReactNode;
}

const Theme = (props: Props) => {
  const {
    toolbarAlways,
    toolbarExtendA,
    toolbarExtend,
    toolbarAutoExtend = [],
    toolbarAutoExtendRow2 = [],
    editorInstance,
    attachment_upload_handler,
    // height,
    customToolbarPlugin,
    allowedFileTypes,
  } = props;
  const [showExtendA, setShowExtendA] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const [showAutoExtend, setShowAutoExtend] = useState(false);
  const [needAutoExtendButton, setNeedAutoExtendButton] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const customPluginRef = useRef<HTMLDivElement>(null);

  const extendAPopoverRef = useRef<HTMLDivElement>(null);
  const extendPopoverRef = useRef<HTMLDivElement>(null);

  // Determine if the auto-extend popover has two rows (placed early for use in callbacks)
  const hasAutoExtendRow2 = toolbarAutoExtendRow2.length > 0;

  // const aiMode = useStore(state => state.editor_ai_mode);
  const extendAction = useCallback(() => {
    setShowExtendA(false);
    setShowExtend(v => {
      const next = !v;
      // 通知父容器：extend 浮层始终单行
      editorToolbarEmitter.emit('toolbarPopoverChange', next ? 1 : 0);
      return next;
    });
  }, []);
  const extendAAction = useCallback(() => {
    setShowExtend(false);
    setShowExtendA(v => {
      const next = !v;
      editorToolbarEmitter.emit('toolbarPopoverChange', next ? 1 : 0);
      return next;
    });
  }, []);
  const autoExtendAction = useCallback(
    (isOpen: boolean) => {
      setShowAutoExtend(isOpen);
      const rows = isOpen ? (hasAutoExtendRow2 ? 2 : 1) : 0;
      editorToolbarEmitter.emit('toolbarPopoverChange', rows);
    },
    [hasAutoExtendRow2]
  );

  // Close ExtendA and Extend popovers when clicking outside (auto extend is excluded)
  useClickOutside(
    extendAPopoverRef,
    useCallback(() => {
      setShowExtendA(false);
      editorToolbarEmitter.emit('toolbarPopoverChange', 0);
    }, []),
    showExtendA
  );

  useClickOutside(
    extendPopoverRef,
    useCallback(() => {
      setShowExtend(false);
      editorToolbarEmitter.emit('toolbarPopoverChange', 0);
    }, []),
    showExtend
  );

  // Calculate if we need the auto-extend button based on container width - with ResizeObserver for responsive behavior
  useEffect(() => {
    if (!toolbarAutoExtend || toolbarAutoExtend.length === 0) {
      setNeedAutoExtendButton(false);
      setIsInitialized(true);
      return;
    }

    const calculateToolbarWidth = () => {
      if (!toolbarRef.current) return;

      // Use requestAnimationFrame to smooth the calculation
      requestAnimationFrame(() => {
        if (!toolbarRef.current) return;

        // Get container width
        const containerWidth = toolbarRef.current.offsetWidth;

        // Estimate the width needed for toolbar items
        // Each toolbar item is approximately 34px (icon + padding)
        // Split is approximately 1px (divider)
        const estimateItemWidth = (items: ToolbarAlwaysType) => {
          const gap = 2;
          return items.reduce((acc, item) => {
            if (item === 'split') return acc + gap + 1;
            if (item === 'fontselect') return acc + gap + 120;
            if (item === 'fontsizeselect') return acc + gap + 80;
            if (item === 'aiWriter') return acc + gap + 100;
            return acc + gap + 26;
          }, 0);
        };

        const alwaysToolbarWidth = estimateItemWidth(toolbarAlways);
        const autoExtendToolbarWidth = estimateItemWidth(toolbarAutoExtend);
        const padding = 32; // Container padding

        // Get actual width of custom plugin if it exists
        const customPluginWidth = customPluginRef.current ? customPluginRef.current.offsetWidth : 0;

        // If all items can fit in one row, no need for the button
        const totalWidth = alwaysToolbarWidth + autoExtendToolbarWidth + padding + customPluginWidth;

        const needButton = totalWidth > containerWidth;

        // Only update if the state actually needs to change to prevent unnecessary re-renders
        setNeedAutoExtendButton(prev => {
          if (prev !== needButton) {
            setShowAutoExtend(!needButton);
            return needButton;
          }
          return prev;
        });

        if (!isInitialized) {
          setIsInitialized(true);
        }
      });
    };

    // Calculate on mount and resize with debouncing for better performance
    let timeoutId: NodeJS.Timeout;
    const debouncedCalculate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateToolbarWidth, 50); // Debounce for smoother resize
    };

    // Initial calculation
    debouncedCalculate();

    // Set up ResizeObserver for responsive behavior
    const resizeObserver = new ResizeObserver(debouncedCalculate);
    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [toolbarAlways, toolbarAutoExtend, customToolbarPlugin, isInitialized]); // Keep necessary dependencies

  // useEffect(() => {
  //   const scrollEle = document.querySelector('.mail-editor .scrollBox');
  //   if (!scrollEle) return;
  //   let newHeight = height || scrollEle.clientHeight;
  //   if (height) {
  //     if (showExtend || showExtendA || showAutoExtend) {
  //       newHeight -= 48 * 2;
  //     } else {
  //       newHeight -= 48;
  //     }
  //   } else {
  //     if (showExtend || showExtendA || showAutoExtend) {
  //       newHeight -= 48;
  //     } else {
  //       newHeight += 48;
  //     }
  //   }
  //   (scrollEle as HTMLElement).style.setProperty('height', `${newHeight}px`);
  // }, [showExtend, showExtendA, showAutoExtend, height]);

  // Initialize component lists - stable but functional
  const compLists = useMemo(
    () => ({
      ...compListsOrg,
      attachment: { comp: Attachment, param: { attachment_upload_handler, allowedFileTypes: allowedFileTypes } },
      extend: { comp: Extend, param: { extendAction } },
      extendA: { comp: ExtendA, param: { extendAAction } },
      autoExtend: { comp: AutoExtend, param: { autoExtendAction } },
    }),
    [attachment_upload_handler, allowedFileTypes, extendAction, extendAAction, autoExtendAction] // Keep necessary dependencies for functionality
  );

  // Calculate toolbar items - responsive to needAutoExtendButton but stable otherwise
  const toolbarItemsConfig = useMemo(() => {
    const baseMainItems = toolbarAlways.filter(item => item !== 'autoExtend') as ToolbarAlwaysType;
    const autoExtendIndex = toolbarAlways.findIndex(item => item === 'autoExtend');

    let mainItemsWithAutoExtend: ToolbarAlwaysType = baseMainItems;
    let mainItemsWithButton: ToolbarAlwaysType = baseMainItems;

    if (toolbarAutoExtend && toolbarAutoExtend.length > 0) {
      if (autoExtendIndex === -1) {
        // If 'autoExtend' is not in toolbarAlways, append autoExtend items
        mainItemsWithAutoExtend = [...toolbarAlways, ...toolbarAutoExtend] as ToolbarAlwaysType;
        mainItemsWithButton = toolbarAlways;
      } else {
        // Split toolbarAlways at the autoExtend position
        const before = toolbarAlways.slice(0, autoExtendIndex);
        const after = toolbarAlways.slice(autoExtendIndex + 1);

        mainItemsWithAutoExtend = [...before, ...toolbarAutoExtend, ...after] as ToolbarAlwaysType;
        mainItemsWithButton = [...before, 'autoExtend' as const, ...after] as ToolbarAlwaysType;
      }
    }

    return {
      withAutoExtend: mainItemsWithAutoExtend,
      withButton: mainItemsWithButton,
      base: baseMainItems,
    };
  }, [toolbarAlways, toolbarAutoExtend]); // Only recalculate when toolbar config changes, not needAutoExtendButton

  return (
    <div
      ref={toolbarRef}
      className={classname(
        'h-full w-full text-gray-800 bg-white flex items-center px-4 relative ',
        props.className
      )}
      // hidden={aiMode !== ''}
      style={{
        borderRadius: '0 0 12px 12px',
      }}
    >
      {/* Main Toolbar - with AutoExtend items or Button */}
      <div className="flex items-center h-8 gap-0.5">
        {(needAutoExtendButton ? toolbarItemsConfig.withButton : toolbarItemsConfig.withAutoExtend).map((i, index) => {
          const item = compLists[i];
          if (!item) return null;
          const Component = item.comp;
          const param = item.param;
          return (
            <Component
              key={`main-${i}-${index}`}
              editorInstance={editorInstance}
              {...param}
              allowedFileTypes={allowedFileTypes}
            />
          );
        })}
      </div>

      {/* Custom Plugin Area */}
      {customToolbarPlugin && (
        <div ref={customPluginRef} className="flex items-center ml-3">
          {customToolbarPlugin}
        </div>
      )}

      {/* Auto Extend Toolbar Popover - supports single row or two rows */}
      {showAutoExtend && needAutoExtendButton && toolbarAutoExtend && toolbarAutoExtend.length > 0 && (
        <div
          className="absolute left-4 bottom-full mb-1 flex flex-col gap-2"
          style={{ zIndex: 9999 }}
        >
          {/* Row 2 (top row - further from toolbar) */}
          {hasAutoExtendRow2 && (
            <div className="bg-[#F5F5F5] px-3 rounded-[22px] h-[34px] flex items-center gap-0.5 w-fit">
              <ToolbarItemsRenderer
                items={toolbarAutoExtendRow2}
                compLists={compLists}
                editorInstance={editorInstance}
                allowedFileTypes={allowedFileTypes}
                keyPrefix="autoExtendRow2"
              />
            </div>
          )}
          {/* Row 1 (bottom row - closer to toolbar) */}
          <div className="bg-[#F5F5F5] px-3 rounded-[22px] h-[34px] flex items-center gap-0.5">
            <ToolbarItemsRenderer
              items={toolbarAutoExtend.slice(1)}
              compLists={compLists}
              editorInstance={editorInstance}
              allowedFileTypes={allowedFileTypes}
              keyPrefix="autoExtend"
            />
          </div>
        </div>
      )}

      {/* ExtendA Toolbar Popover */}
      {showExtendA && toolbarExtendA && toolbarExtendA.length > 0 && (
        <div
          ref={extendAPopoverRef}
          className="absolute left-4 bottom-full mb-1 bg-[#F5F5F5] px-2 rounded-[22px] h-8 flex justify-center items-center"
          style={{ zIndex: 9999 }}
        >
          <div className="flex items-center gap-0.5">
            <ToolbarItemsRenderer
              items={toolbarExtendA}
              compLists={compLists}
              editorInstance={editorInstance}
              allowedFileTypes={allowedFileTypes}
              keyPrefix="extendA"
            />
          </div>
        </div>
      )}

      {/* Extend Toolbar Popover */}
      {showExtend && toolbarExtend && toolbarExtend.length > 0 && (
        <div
          ref={extendPopoverRef}
          className="absolute left-4 bottom-full mb-1 bg-[#F5F5F5] px-2 rounded-[22px] h-8 flex justify-center items-center"
          style={{ zIndex: 9999 }}
        >
          <div className="flex items-center gap-0.5">
            <ToolbarItemsRenderer
              items={toolbarExtend}
              compLists={compLists}
              editorInstance={editorInstance}
              allowedFileTypes={allowedFileTypes}
              keyPrefix="extend"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Theme;
